/**
 * @file Client-side filtering of the drawer's property and unit tree.
 *
 * Every structure and its units are already loaded by `useStructures`, so the
 * drawer search never calls the API. These helpers are pure so they can be
 * unit tested without rendering the drawer.
 */

import type { Address } from '../../types';
import type { Structure } from '../../Structures/types';
import type { Unit } from '../../Units/types';
import { naturalCompare } from '../../util';

/** Which field produced a match. `name` matches need no explanation in the UI. */
export type MatchField = 'name' | 'address' | 'serial' | 'deviceName';

/**
 * Serials are ten-digit strings and addresses hold a street number and zip, so a
 * single character matches almost everything through them. Device names default
 * to the serial, so they wait too. Property and unit names still match from the
 * first character.
 */
export const MIN_SECONDARY_QUERY_LENGTH = 2;

export type UnitMatch = {
  unit: Unit;
  /** Null when the unit is shown only because the query is empty or its property was expanded. */
  matchedBy: MatchField | null;
  /** Text to show under the unit name when it matched by something other than its name. */
  matchText: string | null;
};

export type StructureMatch = {
  structure: Structure;
  /** Null when the structure is shown only because one of its units matched or the query is empty. */
  matchedBy: MatchField | null;
  /** Text to show under the property name when it matched by something other than its name. */
  matchText: string | null;
  /** Units to render under the property. All units for an empty query; matching units otherwise. */
  units: UnitMatch[];
  /**
   * The property's remaining units, in natural order, for when the user expands a
   * property that matched on its own. Empty for an empty query, where `units` already
   * holds everything.
   */
  otherUnits: UnitMatch[];
};

/** Lowercase, trim, and collapse runs of whitespace so "  Unit   12 " matches "unit 12". */
export const normalizeQuery = (query: string): string =>
  query.toLowerCase().trim().replace(/\s+/g, ' ');

/** Single-line address used both for matching and for the secondary row text. */
export const formatAddressLine = (address: Address | null | undefined): string =>
  [address?.street, address?.street2, address?.city, address?.state, address?.zip]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

const contains = (haystack: string | null | undefined, needle: string): boolean =>
  normalizeQuery(haystack ?? '').includes(needle);

/**
 * The API zero-pads serials to ten characters, but meters carry four or five digit
 * numbers, so the padding is noise in a narrow row and lets "00" match every device.
 * Strip it for display and from both sides of the comparison, so a serial copied
 * from Device Settings with its padding still matches.
 */
export const displaySerial = (serial: string | null | undefined): string =>
  (serial ?? '').replace(/^0+(?=\d)/, '');

const serialContains = (serial: string | null | undefined, needle: string): boolean => {
  // Leading zeros in the query are padding unless the query is nothing but zeros, in
  // which case they are a literal run the serial must actually contain.
  const candidate = /^0+$/.test(needle) ? needle : needle.replace(/^0+/, '');
  return candidate !== '' && contains(displaySerial(serial), candidate);
};

const sortedUnits = (units: Unit[] | undefined): Unit[] =>
  (units ?? []).filter(Boolean).sort((a, b) => naturalCompare(a.name, b.name));

const unmatched = (unit: Unit): UnitMatch => ({ unit, matchedBy: null, matchText: null });

const matchUnit = (unit: Unit, needle: string, allowSecondary: boolean): UnitMatch | null => {
  if (contains(unit.name, needle)) {
    return { unit, matchedBy: 'name', matchText: null };
  }
  if (!allowSecondary) return null;
  const devices = unit.devices ?? [];
  // A device's name defaults to its serial, so most devices match both ways. Serial
  // wins so the secondary line stays consistent; name only reports when no serial hit.
  const bySerial = devices.find((d) => serialContains(d.serial_number, needle));
  if (bySerial) {
    return {
      unit,
      matchedBy: 'serial',
      matchText: `Serial ${displaySerial(bySerial.serial_number)}`,
    };
  }
  const byName = devices.find((d) => contains(d.name, needle));
  if (byName) {
    return { unit, matchedBy: 'deviceName', matchText: `Meter ${byName.name}` };
  }
  return null;
};

/**
 * Filter the property tree for the drawer.
 *
 * - Empty query: every structure with every unit, in natural sort order.
 * - Otherwise: structures whose name or address matches, plus structures with at
 *   least one unit whose name, device serial, or device name matches. Only matching units are
 *   listed under each structure, so a property matched by its own name shows a
 *   collapsed row rather than hundreds of unmatched units; the rest are returned
 *   as `otherUnits` so the row can still be expanded on demand.
 * - Address, serial, and device name matching wait for {@link MIN_SECONDARY_QUERY_LENGTH} characters.
 */
export const filterStructures = (
  structures: Structure[] | undefined,
  query: string,
): StructureMatch[] => {
  const needle = normalizeQuery(query);
  const sortedStructures = [...(structures ?? [])].sort((a, b) => naturalCompare(a.name, b.name));

  if (needle === '') {
    return sortedStructures.map((structure) => ({
      structure,
      matchedBy: null,
      matchText: null,
      units: sortedUnits(structure.units).map(unmatched),
      otherUnits: [],
    }));
  }

  const allowSecondary = needle.length >= MIN_SECONDARY_QUERY_LENGTH;
  const results: StructureMatch[] = [];
  for (const structure of sortedStructures) {
    const units: UnitMatch[] = [];
    const otherUnits: UnitMatch[] = [];
    for (const unit of sortedUnits(structure.units)) {
      const match = matchUnit(unit, needle, allowSecondary);
      if (match) units.push(match);
      else otherUnits.push(unmatched(unit));
    }

    let matchedBy: MatchField | null = null;
    let matchText: string | null = null;
    if (contains(structure.name, needle)) {
      matchedBy = 'name';
    } else if (allowSecondary) {
      const addressLine = formatAddressLine(structure.address);
      if (contains(addressLine, needle)) {
        matchedBy = 'address';
        matchText = addressLine;
      }
    }

    if (matchedBy !== null || units.length > 0) {
      results.push({ structure, matchedBy, matchText, units, otherUnits });
    }
  }
  return results;
};

/** Route target for Enter in the search box: the first matching unit, else the first matching property. */
export const firstResult = (
  matches: StructureMatch[],
): { structureId: string; unitId: string | null } | null => {
  const first = matches[0];
  if (!first) return null;
  const unit = first.units[0];
  return { structureId: first.structure.id, unitId: unit ? unit.unit.id : null };
};
